/**
 * Pure logic behind the global calendar: what state an event is in, how it looks, where it
 * leads, and how the classroom filter lives in the URL. Nothing here touches React or the
 * network, so all of it is unit tested directly.
 */
import { startOfNextLocalDay, toLocalDateKey } from '../../../utils/dateUtils';

export const CALENDAR_ROLES = Object.freeze({ TEACHER: 'teacher', STUDENT: 'student' });

export const EVENT_STATUS = Object.freeze({
    UPCOMING: 'upcoming',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    LATE: 'late',
    NO_DUE_DATE: 'no_due_date',
    TEACHER: 'teacher',
});

export const STATUS_LABELS = Object.freeze({
    [EVENT_STATUS.UPCOMING]: 'Upcoming',
    [EVENT_STATUS.IN_PROGRESS]: 'In progress',
    [EVENT_STATUS.COMPLETED]: 'Completed',
    [EVENT_STATUS.LATE]: 'Late',
    [EVENT_STATUS.NO_DUE_DATE]: 'Assigned',
    [EVENT_STATUS.TEACHER]: 'Teaching',
});

/** Within a day, what needs attention first: late work, then what's due, then the rest. */
const STATUS_SORT = Object.freeze({
    [EVENT_STATUS.LATE]: 0,
    [EVENT_STATUS.UPCOMING]: 1,
    [EVENT_STATUS.IN_PROGRESS]: 2,
    [EVENT_STATUS.TEACHER]: 3,
    [EVENT_STATUS.COMPLETED]: 4,
    [EVENT_STATUS.NO_DUE_DATE]: 5,
});

/**
 * Where an event stands for the person looking at it.
 *
 * Only student events with a due date carry the upcoming / submitted / late states. Without
 * a due date there is nothing to be late for, so even a submission doesn't change how it
 * looks. A due date is a whole day: work due today is not late until the next midnight.
 *
 * @param {{role: string, dueDate?: string|null, submission?: {status: string}|null}} event
 * @param {Date} now
 */
export const deriveEventStatus = (event, now) => {
    if (!event.dueDate) return EVENT_STATUS.NO_DUE_DATE;
    if (event.role === CALENDAR_ROLES.TEACHER) return EVENT_STATUS.TEACHER;
    if (event.submission?.status === 'completed') return EVENT_STATUS.COMPLETED;
    if (event.submission) return EVENT_STATUS.IN_PROGRESS;
    return now >= startOfNextLocalDay(event.dueDate) ? EVENT_STATUS.LATE : EVENT_STATUS.UPCOMING;
};

/**
 * Distinct mid-tones that read on both the light and dark themes. Red is left out on
 * purpose — it belongs to "late".
 */
export const CLASSROOM_PALETTE = Object.freeze([
    '#1982c4', // blue
    '#e76f51', // coral
    '#7b5ea7', // purple
    '#2a9d8f', // teal
    '#e9a23b', // amber
    '#c2417b', // magenta
    '#5a8f29', // green
    '#4361ee', // indigo
    '#a0522d', // sienna
    '#0096c7', // cyan
]);

/**
 * A colour per classroom, by position in id order. ObjectIds grow over time, so joining a
 * new classroom gives it the next colour without reshuffling the ones already in use.
 *
 * @returns {Map<string, string>} classroom id -> colour
 */
export const assignClassroomColors = (classrooms = []) => {
    const sortedIds = [...new Set(classrooms.map((c) => String(c._id)))].sort();
    return new Map(sortedIds.map((id, index) => [id, CLASSROOM_PALETTE[index % CLASSROOM_PALETTE.length]]));
};

export const eventTitle = (event) =>
    event.title || event.scenario?.title || 'Untitled assignment';

/**
 * Map an API event to a FullCalendar event.
 *
 * Deliberately no FullCalendar `color`/`backgroundColor`: those paint the whole block and
 * override the transparent defaults. The classroom colour travels in extendedProps and
 * the event content applies it.
 */
export const toCalendarEvent = (event, { colorById, classroomById, now }) => {
    const classroomId = String(event.classroom);
    const status = deriveEventStatus(event, now);

    return {
        id: `${event.role}:${event._id}`,
        title: eventTitle(event),
        start: toLocalDateKey(event.dueDate || event.createdAt),
        allDay: true,
        classNames: [`calendar-role-${event.role}`, `calendar-status-${status}`],
        extendedProps: {
            assignmentId: String(event._id),
            classroomId,
            classroomName: classroomById.get(classroomId)?.name || 'Classroom',
            classroomColor: colorById.get(classroomId) || CLASSROOM_PALETTE[0],
            role: event.role,
            status,
            scenarioTitle: event.scenario ? event.scenario.title || 'Untitled scenario' : null,
            sortKey: STATUS_SORT[status],
        },
    };
};

/** The accessible one-line description of an event: "Late · Essay 1 · Biology 101". */
export const describeEvent = ({ title, extendedProps }) =>
    [STATUS_LABELS[extendedProps.status], title, extendedProps.classroomName].join(' · ');

// --- The classroom filter, kept in the URL ------------------------------------------------

export const CLASSROOM_FILTER_PARAM = 'classrooms';

/** @returns {string[]} The selected classroom ids; empty means "all". */
export const parseClassroomFilter = (searchParams) => [
    ...new Set(
        (searchParams.get(CLASSROOM_FILTER_PARAM) || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
    ),
];

/** A copy of `searchParams` with the filter set to `ids` (removed entirely when empty). */
export const withClassroomFilter = (searchParams, ids) => {
    const next = new URLSearchParams(searchParams);
    if (ids.length > 0) next.set(CLASSROOM_FILTER_PARAM, ids.join(','));
    else next.delete(CLASSROOM_FILTER_PARAM);
    return next;
};

// --- Where an event leads -----------------------------------------------------------------

/**
 * Where clicking an event goes, as `{ pathname, state }` for `navigate`.
 *
 * The two roles are kept as separate branches on purpose even though they agree today, so
 * either destination can change on its own.
 */
export const getEventDestination = ({ role, classroomId, assignmentId }) => {
    const state = { focusAssignmentId: assignmentId };

    if (role === CALENDAR_ROLES.TEACHER) {
        // The place to send teachers somewhere else, e.g. the submissions page at
        // `/my-classrooms/${classroomId}/assignments/${assignmentId}`.
        return { pathname: `/my-classrooms/${classroomId}/assignments`, state };
    }

    return { pathname: `/my-classrooms/${classroomId}/assignments`, state };
};
